import { Link, useParams } from "react-router-dom";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { HardHat, ArrowLeft, Clock } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "./FAQ";
import NotFound from "./seo/NotFound";
import { BLOG_POSTS, getPost, formatDate } from "@/lib/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <NotFound />;

  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      datePublished: post.date,
      dateModified: post.date,
      inLanguage: "en-NZ",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.fieldlink.co.nz/blog/${post.slug}` },
      author: { "@type": "Organization", name: "Fieldlink NZ", url: "https://www.fieldlink.co.nz" },
      publisher: {
        "@type": "Organization",
        name: "Fieldlink NZ",
        logo: { "@type": "ImageObject", url: "https://www.fieldlink.co.nz/icon-512.png" },
      },
      image: "https://www.fieldlink.co.nz/icon-512.png",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.fieldlink.co.nz/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.fieldlink.co.nz/blog" },
        { "@type": "ListItem", position: 3, name: post.title, item: `https://www.fieldlink.co.nz/blog/${post.slug}` },
      ],
    },
  ];

  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO title={`${post.title} | Fieldlink NZ Blog`} description={post.description} path={`/blog/${post.slug}`} jsonLd={jsonLd} />

      <MarketingHeader />

      <article className="max-w-3xl mx-auto px-6 md:px-12 py-12" data-testid="blog-post-article">
        <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#52616c] hover:text-[#a63c0b] mb-6" data-testid="post-back-link">
          <ArrowLeft className="w-4 h-4" /> All articles
        </Link>
        <div className="flex flex-wrap items-center gap-3 mb-4 text-[10px] font-bold uppercase tracking-widest">
          <span className="bg-[#FF5F15] text-[#142635] px-2 py-0.5">{post.tag}</span>
          <span className="text-[#52616c]">{formatDate(post.date)}</span>
          <span className="text-[#52616c] flex items-center gap-1"><Clock className="w-3 h-3" />{post.readMins} min read</span>
        </div>
        <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight mb-8">{post.title}</h1>

        {post.pricingNote && (
          <div className="rounded-r-md border-l-4 border-[#FF5F15] bg-[#FF5F15]/10 px-4 py-3 mb-8 flex flex-wrap items-center justify-between gap-3" data-testid="post-pricing-note">
            <p className="text-sm text-[#465562]">
              <b className="uppercase tracking-wider text-xs text-[#a63c0b] block mb-0.5">Pricing updated</b>
              Software pricing (including Fieldlink&apos;s) may have changed since this article was published.
            </p>
            <Link to="/pricing" className="rounded-md text-xs font-bold uppercase tracking-wider bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] px-4 py-2 shrink-0">View current pricing</Link>
          </div>
        )}

        <div className="space-y-5">
          {post.blocks.map((b, i) => {
            if (b.t === "link") return <p key={i} className="text-[#465562] leading-relaxed">{b.c} <Link to={b.to} className="underline underline-offset-4">{b.label}</Link>.</p>;
            if (b.t === "h2") return <h2 key={i} className="font-display font-black uppercase tracking-tight text-2xl pt-4">{b.c}</h2>;
            if (b.t === "ul") return (
              <ul key={i} className="space-y-2">
                {b.c.map((item, j) => (
                  <li key={j} className="flex items-start gap-3 text-[#465562] leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-[#FF5F15] shrink-0" />{item}
                  </li>
                ))}
              </ul>
            );
            if (b.t === "quote") return (
              <blockquote key={i} className="border-l-4 border-[#FF5F15] pl-5 py-1 font-display font-bold uppercase tracking-tight text-lg text-[#344451]">{b.c}</blockquote>
            );
            return <p key={i} className="text-[#465562] leading-relaxed">{b.c}</p>;
          })}
        </div>
      </article>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] text-[#142635] text-center py-16">
        <h2 className="font-display font-black uppercase tracking-tight text-3xl mb-3">See it working on your jobs</h2>
        <p className="text-[#465562] mb-6">NZ-made. NZD pricing. 7 days free, no card needed.</p>
        <Link to="/register" className="inline-block rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-8 py-4" data-testid="post-footer-cta">Start free trial →</Link>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-14 border-t border-[#cbd3d5]">
        <h2 className="font-display font-black uppercase tracking-tight text-2xl mb-6">Keep reading</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {others.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="rounded-lg border border-[#cbd3d5] bg-white hover:border-[#FF5F15]/60 p-5 transition-colors block" data-testid={`post-related-${p.slug}`}>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#a63c0b] mb-2">{p.tag}</div>
              <div className="font-display font-bold uppercase tracking-tight text-sm leading-snug">{p.title}</div>
            </Link>
          ))}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
